import { useEffect } from 'react';
import { router } from '~/routes/router';
import layerConfigSync, { type LayerConfigSyncController } from '~/store2/layerConfigSync';
import { bindVisibilityAndUnloadFlush, type ListenerTarget } from './bindVisibilityAndUnloadFlush';

/** The slice of the TanStack router the flush wiring needs (its one framework coupling). */
type NavigationSource = {
  subscribe: (event: 'onBeforeNavigate', callback: () => void) => () => void;
};

/**
 * Wire the sync controller's lifecycle flushes and return the teardown.
 * Extracted from the `useLayerConfigSync` effect so the wiring is unit-testable without a
 * React renderer (same pattern as `switchLayerConfigOrganization`).
 *
 * All three boundaries flush via `flushNow()` (a normal PUT): `visibilitychange`→hidden,
 * `beforeunload`, and router `onBeforeNavigate`. The page usually stays alive (tab hidden /
 * in-app nav) so the write completes; on a true teardown the request may be cancelled, which
 * is acceptable — teardown persistence is best-effort.
 *
 * Every flush is internally gated on `canPersist` + dirty, so this wiring is inert until a
 * successful hydrate arms persistence (a failed read can never cascade into an overwrite).
 */
export function wireLayerConfigSync(
  controller: Pick<LayerConfigSyncController, 'start' | 'stop' | 'flushNow'> = layerConfigSync,
  navigationSource: NavigationSource = router,
  documentTarget: ListenerTarget & { hidden: boolean } = document,
  windowTarget: ListenerTarget = window,
): () => void {
  controller.start();

  const unbind = bindVisibilityAndUnloadFlush(controller, documentTarget, windowTarget);
  const unsubscribeNavigation = navigationSource.subscribe('onBeforeNavigate', () => {
    controller.flushNow();
  });

  return () => {
    unbind();
    unsubscribeNavigation();
    controller.stop();
  };
}

/**
 * Mount the debounced sync once near the app root, beside `useLayerConfigBootstrap`
 * (see App2). Registers the store subscriptions and lifecycle flush listeners; tears them
 * down on unmount. The controller is a module singleton with idempotent `start`, so a
 * remount re-wires cleanly.
 */
export function useLayerConfigSync() {
  useEffect(() => wireLayerConfigSync(), []);
}
